import {
  type AnchorHTMLAttributes,
  type ReactNode,
  createContext,
  use,
} from 'react'
import cx from 'classnames'
import { type LinkProps as RRLinkProps } from 'react-router'
import { Icon } from '@/components/Icon'
import { Link } from '@/components/Link'
import { RawButton } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import type { TableSortOrder } from '@/components/Table'
import {
  type AlignmentProps,
  type CellProps,
  type CheckCellProps,
  type ColumnDensity,
  type TestIdProps,
  DateDisplay,
  ImageCellContent,
  type ImageCellProps,
  TextCellContent,
} from '@/components/Table/TableCell'
import { match } from '@/utils/pattern-match-util'
import styles from './ClickableTable.module.css'

interface ClickableTableBaseProps {
  children?: ReactNode
  className?: string
  testId?: string
}

interface ClickableTableProps extends ClickableTableBaseProps {
  striped?: boolean
  stickyHeader?: boolean
}

/**
 * A table whose rows are links.
 */
export function ClickableTable({
  children,
  striped,
  stickyHeader,
  className,
  testId,
}: ClickableTableProps) {
  return (
    <div
      data-test={testId}
      className={cx(
        styles.base,
        striped && styles.striped,
        stickyHeader && styles.stickyHeader,
        className,
      )}
    >
      {children}
    </div>
  )
}

type ClickableTableHeadProps = ClickableTableBaseProps

/**
 * The header row of a {@link ClickableTable}.
 */
export function ClickableTableHead({
  children,
  className,
}: ClickableTableHeadProps) {
  return (
    <div className={cx(styles.base, styles.tableHeader, className)}>
      {children}
    </div>
  )
}

interface ClickableTableHeaderProps
  extends ClickableTableBaseProps, AlignmentProps {
  /**
   * If a TableHeader `canSort` then it will display a sort indicator and
   * make the header clickable
   */
  canSort?: boolean

  /**
   * If a sort order is specified, a sort order indicator will be displayed
   * This will replace the default hasOrder indicator
   */
  sortOrder?: TableSortOrder

  /**
   * A callback that will be called with the next sortOrder when
   * this table header is clicked
   */
  onSort?: (sortOrder: TableSortOrder) => void
}

/**
 * A column header of a {@link ClickableTable}, which can sort it.
 */
export function ClickableTableHeader({
  className,
  children,
  canSort = false,
  sortOrder = 'asc',
  onSort = () => null,
  alignment,
}: ClickableTableHeaderProps) {
  return (
    <div
      className={cx(
        styles.tableHeaderCell,
        className,
        canSort && styles.sortableHeader,
        alignment && styles[alignment],
      )}
    >
      {canSort ? (
        <RawButton
          className={styles.headerWithSort}
          onPress={() =>
            onSort(match(sortOrder, { none: 'asc', asc: 'desc', desc: 'none' }))
          }
        >
          {children}
          <span
            className={cx(
              styles.sortIcon,
              sortOrder === 'asc' && styles.sortIconAsc,
              sortOrder === 'desc' && styles.sortIconDesc,
            )}
          >
            <Icon
              size={14}
              name="sorting"
              rotate={sortOrder !== 'desc' ? -180 : 0}
            />
          </span>
        </RawButton>
      ) : (
        <span>{children}</span>
      )}
    </div>
  )
}

type ClickableTableBodyProps = ClickableTableBaseProps

/**
 * The body of a {@link ClickableTable}.
 */
export function ClickableTableBody({
  children,
  className,
}: ClickableTableBodyProps) {
  return <div className={cx(styles.tableBody, className)}>{children}</div>
}

interface ClickableTableRowProps
  extends
    ClickableTableBaseProps,
    AnchorHTMLAttributes<HTMLAnchorElement>,
    Partial<RRLinkProps> {
  active?: boolean
  alwaysShowPointer?: boolean
}

/**
 * A row of a {@link ClickableTable}, rendered as a link.
 */
export function ClickableTableRow({
  className,
  children,
  active,
  alwaysShowPointer,
  ...rest
}: ClickableTableRowProps) {
  return (
    <Link
      {...rest}
      className={cx(
        styles.tableRow,
        active && styles.active,
        alwaysShowPointer && styles.rowAlwaysShowPointer,
        className,
      )}
    >
      {children}
    </Link>
  )
}

/**
 * The element a {@link ClickableTableCell} renders as: `td` when its cells are
 * placed in a real table row, which can't contain a `div`.
 */
export const ClickableTableCellElementContext = createContext<'div' | 'td'>(
  'div',
)

type ClickableTableCellProps = ClickableTableBaseProps

/**
 * A cell of a {@link ClickableTable}.
 */
export function ClickableTableCell({
  className,
  children,
}: ClickableTableCellProps) {
  const cellClassName = cx(styles.tableCell, className)
  return use(ClickableTableCellElementContext) === 'td' ? (
    <td className={cellClassName}>{children}</td>
  ) : (
    <div className={cellClassName}>{children}</div>
  )
}

interface ClickableTextCellProps
  extends CellProps, AlignmentProps, TestIdProps {}

/**
 * A {@link ClickableTable} cell of text.
 */
export function ClickableTextCell({
  children,
  alignment,
  testId,
}: ClickableTextCellProps) {
  return (
    <ClickableTableCell
      className={cx(styles.textCell, alignment && styles[alignment])}
    >
      <TextCellContent testId={testId}>{children}</TextCellContent>
    </ClickableTableCell>
  )
}

interface ClickableDateCellProps extends TestIdProps {
  date: Date | null
  density: ColumnDensity
}

/**
 * A {@link ClickableTable} cell showing a date.
 */
export function ClickableDateCell({
  date,
  density,
  testId,
}: ClickableDateCellProps) {
  return (
    <ClickableTextCell testId={testId}>
      {date && <DateDisplay date={date} density={density} />}
    </ClickableTextCell>
  )
}

/**
 * A {@link ClickableTable} cell showing an avatar and a title.
 */
export function ClickableImageCell({
  title,
  image,
  imageFallback,
  testId,
}: ImageCellProps) {
  return (
    <ClickableTableCell>
      <ImageCellContent
        title={title}
        image={image}
        imageFallback={imageFallback}
        testId={testId}
      />
    </ClickableTableCell>
  )
}

interface ClickableCheckboxCellProps extends TestIdProps {
  checked?: boolean

  onChange?(checked: boolean): unknown
}

/**
 * A {@link ClickableTable} cell with a checkbox.
 */
export function ClickableCheckboxCell({
  checked,
  onChange,
  testId,
}: ClickableCheckboxCellProps) {
  return (
    <ClickableTableCell>
      <Checkbox
        checked={checked}
        onChange={onChange}
        muteClickEvent
        testId={testId}
      />
    </ClickableTableCell>
  )
}

interface ClickableCheckCellProps extends CheckCellProps, AlignmentProps {}

/**
 * A {@link ClickableTable} cell showing a check mark when checked.
 */
export function ClickableCheckCell({
  checked,
  color,
  alignment,
}: ClickableCheckCellProps) {
  return (
    <ClickableTableCell>
      <div className={cx(alignment && styles[alignment])}>
        {checked ? <Icon name="tick" size={24} color={color} /> : null}
      </div>
    </ClickableTableCell>
  )
}
